
import {
  computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import type { NodeWarning, WarningSeverity } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import type { FlowStructureDto } from '@/types/flow-structure';
import { getOperatorById } from '@/components/flows/operators/registry';
import FlowAnalyticsChip from '@/components/flows/FlowAnalyticsChip.vue';
import { useGlobalFlowAnalytics, OperatorAnalytics } from '@/composables/flows/useFlowAnalytics';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'FlowNode',

  components: {
    FlowAnalyticsChip,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    node: {
      type: Object as PropType<{
        step: FlowStructureDto['steps'][number];
        label: string;
        subtitle: string;
        icon: string;
        kind: string;
        left: number;
        top: number;
        hasInput: boolean;
        outputs: number;
        connectedOutputs: number[];
        warnings: NodeWarning[];
        persistenceList: Record<string, string[]>;
        effectiveHeight: number;
      }>,

      required: true,
    },

    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },

    width: {
      type: Number,
      required: true,
    },

    height: {
      type: Number,
      required: true,
    },

    targetCalculation: {
      type: [Object, String] as PropType<{ in: number | null; out_1?: number; out_2?: number; timeout_exceeded?: boolean } | 'loading' | null>,
      default: null,
    },

    // Selection mode props
    selectionMode: {
      type: Boolean,
      default: false,
    },

    isSelectable: {
      type: Boolean,
      default: false,
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    isHighlighted: {
      type: Boolean,
      default: false,
    },

    isMergeTarget: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['add-before', 'add-after', 'click', 'edit', 'replace', 'delete', 'menu-action', 'warning-action', 'toggle-enabled', 'selection-toggle', 'mouseenter', 'mouseleave', 'resize'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const rootEl = ref<HTMLElement | null>(null);
    const menuOpen = ref(false);
    const confirmingDelete = ref(false);
    const deleteBlockedMessage = ref<string | null>(null);

    // Analytics mode
    const analytics = useGlobalFlowAnalytics();
    const isAnalyticsMode = computed(() => analytics.isAnalyticsMode.value);
    const operatorAnalytics = computed((): OperatorAnalytics | null => analytics.getOperatorAnalytics(props.node.step.id));

    // Computed properties from node
    const label = computed(() => props.node.label);
    const subtitle = computed(() => props.node.subtitle);
    const icon = computed(() => props.node.icon);
    const kind = computed(() => props.node.kind);
    const hasInput = computed(() => props.node.hasInput);
    const outputs = computed(() => props.node.outputs);
    const connectedOutputs = computed(() => props.node.connectedOutputs);

    const nodeStyle = computed(() => ({
      top: `${props.node.top}px`,
      left: `${props.node.left}px`,
      width: `${props.width}px`,
    }));

    // Warning computed properties - only show 'box' warnings on the node
    const boxWarnings = computed(() => props.node.warnings.filter((w) => w.location === 'box'));
    const hasWarnings = computed(() => boxWarnings.value.length > 0);

    // Check if this node is the root (no parent in the flow)
    const isRootNode = computed(() => {
      if (!props.flow) return false;
      const stepId = props.node.step.id;
      // Root node has no connections pointing to it
      return !props.flow.connections.some((conn) => conn.childId === stepId);
    });

    // Hide replace menu for root node in bulk campaigns
    const canReplace = computed(() => {
      if (props.flow?.type === TypeCampaignEnum.BULK && isRootNode.value) {
        return false;
      }
      return true;
    });

    // Persistence computed properties
    const persistenceList = computed(() => props.node.persistenceList);
    const hasPersistence = computed(() => {
      const list = persistenceList.value;
      if (!list) return false;
      // Check if there are any non-empty arrays in the persistence list
      return Object.values(list).some((arr) => arr && arr.length > 0);
    });
    const persistenceCount = computed(() => {
      const list = persistenceList.value;
      if (!list) return 0;
      // Count total segments across all keys
      return Object.values(list).reduce((total, segments) => total + (segments?.length || 0), 0);
    });
    const persistenceTitle = computed(() => t('automatedScenarios.tooltips.persistence.title', persistenceCount.value));
    const flatPersistenceList = computed(() => {
      const list = persistenceList.value;
      if (!list) return [];
      return Object.values(list).flat();
    });

    // Get human-readable label for an operator ID (mainType)
    const getOperatorLabel = (operatorId: string): string => {
      const key = `automatedScenarios.tooltips.persistence.mainType.${operatorId}`;
      const translated = t(key);
      // If translation not found, return the operatorId as fallback
      return translated !== key ? translated : operatorId;
    };

    // Can be a translation key or a step ID from the flow
    const getSegmentLabel = (segmentId: string): string => {
      // Check if segmentId is a step ID in the current flow
      if (props.flow) {
        const step = props.flow.steps.find((s) => s.id === segmentId);
        if (step) {
          // Get the operator and use its getDisplayLabel
          const stepOperator = getOperatorById(step.type);
          if (stepOperator?.getDisplayLabel) {
            const displayLabel = stepOperator.getDisplayLabel(step.parameters || {}, t);
            if (displayLabel) return displayLabel;
          }
          // Fallback to operator label
          return stepOperator?.label ? t(stepOperator.label) : segmentId;
        }
      }

      // Fallback to translation key
      const key = `automatedScenarios.tooltips.persistence.subType.${segmentId}`;
      const translated = t(key);
      // If translation not found, return the segmentId as fallback
      return translated !== key ? translated : segmentId;
    };

    const warningSeverity = computed((): WarningSeverity => {
      if (boxWarnings.value.some((w) => w.severity === 'error')) return 'error';
      if (boxWarnings.value.some((w) => w.severity === 'warning')) return 'warning';
      return 'info';
    });

    // Get custom menu actions from operator
    const operator = computed(() => getOperatorById(props.node.step.type));
    const customMenuActions = computed(() => operator.value?.menuActions || []);

    // Enabled operator toggle (only for automation and operators with canToggleEnabled)
    const canToggleEnabled = computed(() => props.flow?.type === TypeCampaignEnum.AUTOMATION && operator.value?.canToggleEnabled === true);

    const enabledOperator = computed(() => {
      const params = props.node.step.parameters;
      if (!params) return 1;
      // Parameters can be object Record<string, string> or array [{key, value}]
      if (Array.isArray(params)) {
        const enabledParam = (params as Array<{key: string; value: string}>).find((p) => p.key === 'enabled_operator');
        return enabledParam ? parseInt(enabledParam.value, 10) : 1;
      }
      // Object format
      const value = (params as Record<string, string>).enabled_operator;
      return value !== undefined ? parseInt(value, 10) : 1;
    });

    // Tracks whether the current disabled state was set by the system (blocking
    // warnings) vs by the user. Used to auto-re-enable when the warnings clear,
    // without overwriting the user's explicit "inactive" intent.
    const systemAutoDisabled = computed(() => {
      const params = props.node.step.parameters;
      if (!params) return false;
      const raw = Array.isArray(params)
        ? (params as Array<{ key: string; value: string }>).find((p) => p.key === 'system_auto_disabled')?.value
        : (params as Record<string, string>).system_auto_disabled;
      return raw === '1' || raw === 'true';
    });

    // Check if there are blocking warnings (prevents enabling)
    const hasBlockingWarnings = computed(() => boxWarnings.value.some((w) => w.blocking === true));

    // Auto-disable / auto-re-enable based on blocking warnings.
    // system_auto_disabled distinguishes system-triggered disable (re-enable when
    // the warning clears) from user-triggered disable (stays disabled).
    watch(hasBlockingWarnings, (hasBlocking) => {
      if (hasBlocking && enabledOperator.value === 1) {
        emit('toggle-enabled', {
          stepId: props.node.step.id,
          enabled: 0,
          systemAutoDisabled: true,
        });
      } else if (!hasBlocking && enabledOperator.value === 0 && systemAutoDisabled.value) {
        emit('toggle-enabled', {
          stepId: props.node.step.id,
          enabled: 1,
          systemAutoDisabled: false,
        });
      }
    }, { immediate: true });

    // Dropdown is locked when there are blocking warnings
    const isDropdownLocked = computed(() => hasBlockingWarnings.value);

    // Visual dim when the box is disabled by the user (not by the system).
    // System-disabled boxes already show a red warning banner; user-disabled
    // boxes otherwise look identical to active ones, so we dim the content.
    // Restricted to operators the user can actually toggle (automation +
    // metadata flag) — otherwise a stale `enabled_operator: 0` on a bulk
    // trigger would dim a box the user has no way to re-enable.
    const isUserDisabled = computed(() => canToggleEnabled.value
        && enabledOperator.value === 0
        && !hasBlockingWarnings.value);

    const enabledDropdownOpen = ref(false);

    const toggleEnabledDropdown = (e: Event) => {
      e.stopPropagation();
      if (props.isLoading) return;
      // Don't open dropdown if locked
      if (isDropdownLocked.value) return;
      enabledDropdownOpen.value = !enabledDropdownOpen.value;
    };

    const setEnabledOperator = (value: number) => {
      enabledDropdownOpen.value = false;
      // User action: clear the auto-disable flag so the system won't later
      // re-enable a box the user explicitly turned off, and won't forget the
      // explicit intent when the user re-enables.
      emit('toggle-enabled', {
        stepId: props.node.step.id,
        enabled: value,
        systemAutoDisabled: false,
      });
    };

    const toggleMenu = () => {
      if (props.isLoading) return;
      menuOpen.value = !menuOpen.value;
      if (!menuOpen.value) {
        confirmingDelete.value = false;
      }
    };

    const closeMenu = () => {
      menuOpen.value = false;
      confirmingDelete.value = false;
    };

    const handleEdit = () => {
      closeMenu();
      emit('click');
    };

    const handleReplace = () => {
      closeMenu();
      emit('replace');
    };

    const handleMenuAction = (actionId: string) => {
      closeMenu();
      emit('menu-action', actionId, props.node.step);
    };

    const handleWarningAction = (event: string) => {
      emit('warning-action', event, props.node.step);
    };

    const showDeleteConfirm = async () => {
      // Check if deletion is allowed
      deleteBlockedMessage.value = null;
      const operator = getOperatorById(props.node.step.type);
      if (operator?.canBeDeleted && props.flow) {
        const result = await operator.canBeDeleted(props.node.step.id, props.flow);
        if (result !== true) {
          deleteBlockedMessage.value = typeof result === 'string' ? result : t('automatedScenarios.cant_remove_operator');
        }
      }
      confirmingDelete.value = true;
    };

    const cancelDelete = () => {
      confirmingDelete.value = false;
    };

    const confirmDelete = () => {
      closeMenu();
      emit('delete');
    };

    // Handle node click - in selection mode, toggle selection; otherwise emit click
    // In analytics mode, do nothing (no editing allowed)
    // Deprecated / not-available-here operators stay clickable so users can
    // open the form, inspect what was configured, and migrate to the
    // replacement filter before deleting.
    const handleNodeClick = () => {
      if (props.isLoading) return;
      if (isAnalyticsMode.value) {
        // Do nothing in analytics mode
        return;
      }
      if (props.selectionMode) {
        if (props.isSelectable) {
          emit('selection-toggle');
        }
        // Do nothing if not selectable in selection mode
      } else {
        emit('click');
      }
    };

    // Close menu and enabled dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.flow-node__actions') && !target.closest('.flow-node__menu')) {
        closeMenu();
      }
      if (!target.closest('.flow-node__enabled-toggle')) {
        enabledDropdownOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);

      // Monitor size changes (especially for analytics chips expansion)
      if (rootEl.value) {
        const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
          for (const entry of entries) {
            // Use borderBoxSize if available for better accuracy, fall back to offsetHeight
            let height = 0;
            if (entry.borderBoxSize && entry.borderBoxSize.length > 0) {
              height = entry.borderBoxSize[0].blockSize;
            } else {
              height = (entry.target as HTMLElement).offsetHeight;
            }

            // Emit resize event
            emit('resize', {
              stepId: props.node.step.id,
              height,
            });
          }
        });
        resizeObserver.observe(rootEl.value);

        // Cleanup observer on unmount
        onBeforeUnmount(() => {
          resizeObserver.disconnect();
          document.removeEventListener('click', handleClickOutside);
        });
      }
    });

    onBeforeUnmount(() => {
      // document listener removed in resize cleanup block if initialized, acting as backup
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      t,
      label,
      subtitle,
      icon,
      kind,
      hasInput,
      outputs,
      connectedOutputs,
      nodeStyle,
      menuOpen,
      confirmingDelete,
      deleteBlockedMessage,
      toggleMenu,
      handleEdit,
      handleReplace,
      canReplace,
      handleMenuAction,
      handleWarningAction,
      showDeleteConfirm,
      cancelDelete,
      confirmDelete,
      // Warnings
      boxWarnings,
      hasWarnings,
      warningSeverity,
      isRootNode,
      // Persistence
      persistenceList,
      hasPersistence,
      persistenceTitle,
      flatPersistenceList,
      getSegmentLabel,
      // Custom menu actions
      customMenuActions,
      // Target calculation
      targetCalculation: computed(() => props.targetCalculation),
      // Enabled operator toggle
      canToggleEnabled,
      enabledOperator,
      enabledDropdownOpen,
      toggleEnabledDropdown,
      setEnabledOperator,
      isDropdownLocked,
      isUserDisabled,
      // Selection mode
      handleNodeClick,
      selectionMode: computed(() => props.selectionMode),
      isSelectable: computed(() => props.isSelectable),
      isSelected: computed(() => props.isSelected),
      // Analytics mode
      isAnalyticsMode,
      operatorAnalytics,
      rootEl,
    };
  },
});
